/**
 * Full document navigation that SPA / App Bridge soft redirects often cannot
 * recover from (chunk load failure, stale deploy, bfcache).
 *
 * Prefer form GET submit over `location.href` so routers are less likely to
 * intercept. Falls back to `location.replace` + `reload` if navigation hangs.
 */
export function forceFullPageReload(): void {
  if (typeof window === 'undefined') return

  const win = window as Window & {
    __FORDEER_FORCE_RELOADING__?: boolean
  }
  if (win.__FORDEER_FORCE_RELOADING__) return
  win.__FORDEER_FORCE_RELOADING__ = true

  try {
    const u = new URL(window.location.href)
    // Cache-bust: force a fresh document (no bfcache / soft nav)
    u.searchParams.set('_fr', String(Date.now()))

    let navWindow: Window = window
    try {
      if (
        window.top &&
        window.top !== window &&
        window.top.location.hostname === window.location.hostname
      ) {
        navWindow = window.top
      }
    } catch {
      // cross-origin top — stay on current window (typical Shopify iframe)
    }

    const doc = navWindow.document
    // Form GET submit = full document navigation; SPA harder to intercept
    const form = doc.createElement('form')
    form.method = 'GET'
    form.action = u.pathname
    form.acceptCharset = 'UTF-8'
    form.style.display = 'none'
    u.searchParams.forEach((value, key) => {
      const input = doc.createElement('input')
      input.type = 'hidden'
      input.name = key
      input.value = value
      form.appendChild(input)
    })
    ;(doc.body || doc.documentElement).appendChild(form)
    form.submit()

    // Fallback if still hung / blank (navigation blocked)
    window.setTimeout(() => {
      try {
        if (doc.visibilityState !== 'hidden') {
          navWindow.location.replace(u.toString())
          window.setTimeout(() => {
            try {
              navWindow.location.reload()
            } catch {
              window.location.reload()
            }
          }, 400)
        }
      } catch {
        window.location.href = u.toString()
        window.location.reload()
      }
    }, 1000)
  } catch {
    win.__FORDEER_FORCE_RELOADING__ = false
    try {
      const fallback = new URL(window.location.href)
      fallback.searchParams.set('_fr', String(Date.now()))
      window.location.replace(fallback.toString())
    } catch {
      window.location.reload()
    }
  }
}
